import { normalizeMobileNumber } from './mobileNumber';

export type SuggestionField = 'name' | 'reference';
export type SuggestionSource = 'anumati' | 'mjbj';

export interface SuggestionRecord {
	id: string;
	name?: string;
	reference?: string;
	phone?: string;
	title?: string;
	old_name?: string;
	contacts?: { Mobile?: string[] } | string;
	image?: string;
	card_no?: string | number;
	permission_date?: string;
	createdTime?: number;
	createdAt?: { seconds: number; nanoseconds?: number };
	updatedAt?: { seconds: number; nanoseconds?: number };
	deleted?: boolean;
	active?: boolean;
	status?: string;
}

export interface SuggestionSnapshot {
	// Null means unavailable, not an empty but available source.
	history: readonly SuggestionRecord[] | null;
	directory: readonly SuggestionRecord[] | null;
	session: unknown;
}

export interface RecordSuggestion {
	id: string;
	value: string;
	sources: SuggestionSource[];
	phones: string[];
	historyCount: number;
	recordCount: number;
	date?: string;
	image?: string;
	matchReason?: string;
}

interface PreparedSuggestion extends RecordSuggestion {
	key: string;
	words: string[];
	title: string;
	aliases: { key: string; label: string }[];
	activityTime: number;
	creationTime: number;
	imageConflict?: boolean;
}

export interface SuggestionResults {
	suggestions: RecordSuggestion[];
	total: number;
	unavailableSources: SuggestionSource[];
}

const TITLES: { [title: string]: string } = {
	m: 'Mahatma', mh: 'Mahatma', mahatma: 'Mahatma',
	b: 'Bai', bai: 'Bai', bh: 'Bhagat', bhagat: 'Bhagat', dada: 'Dada', dadi: 'Dadi',
};
const DISPLAY_TITLES: { [title: string]: string } = { Mahatma: 'Mh.', Bhagat: 'Bh.' };
const RESULT_LIMIT = 20;
const CACHE_LIMIT = 50;

function searchText(value: string) {
	return value.normalize('NFKC').toLowerCase().replace(/\s+/g, ' ').trim()
		// Missing spaces after these honorifics must not turn a word match into a substring.
		.replace(/(^|\s)(ku|smt)\.\s*(?=\S)/g, '$1$2. ');
}

function parseName(value: string) {
	let text = searchText(value);
	const prefix = /^(mahatma|mh|m|bai|b|bhagat|bh|dada|dadi)[.\s]+(?=\S)/.exec(text);
	let title = prefix ? TITLES[prefix[1]] : '';
	if (prefix) text = text.slice(prefix[0].length);
	if (/^(?:mh|m|mahatma|b|bai|bh|bhagat|dada|dadi)\.?$/.test(text)) {
		return { key: '', title: TITLES[text.replace('.', '')] ?? title };
	}
	return { key: text.replace(/\s+ji\.?$/, '').trim(), title };
}

export function normalizeSuggestionName(value: string) {
	return parseName(value).key;
}

function directoryName(record: SuggestionRecord) {
	let value = record.name?.trim() ?? '';
	if (!value) return '';
	const title = record.title?.trim();
	if (title && !parseName(value).title) value = `${DISPLAY_TITLES[title] ?? title} ${value}`;
	return /\bji\.?$/i.test(value) ? value : `${value} Ji`;
}

function eligible(record: SuggestionRecord) {
	return record?.id && record.deleted !== true && record.active !== false &&
		(!record.status || record.status === 'active');
}

function directoryPhones(record: SuggestionRecord) {
	const contacts = record.contacts;
	const phones = contacts && typeof contacts === 'object' && Array.isArray(contacts.Mobile) ? contacts.Mobile : [];
	return Array.from(new Set(phones.map((phone) => normalizeMobileNumber(phone)).filter((phone): phone is string => !!phone)));
}

function recordDate(value?: string) {
	if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return undefined;
	const date = new Date(value + 'T00:00:00Z');
	return Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === value ? value : undefined;
}

function timestampMillis(time?: SuggestionRecord['createdAt']) {
	return time && Number.isFinite(time.seconds) ?
		time.seconds * 1000 + (time.nanoseconds ?? 0) / 1000000 : 0;
}

function recordTimes(record: SuggestionRecord) {
	const creationTime = Math.max(
		Number.isFinite(record.createdTime) ? record.createdTime * 1000 : 0,
		timestampMillis(record.createdAt)
	);
	return { creationTime, activityTime: Math.max(creationTime, timestampMillis(record.updatedAt)) };
}

function compareRecency(a: PreparedSuggestion, b: PreparedSuggestion) {
	return (b.date ?? '').localeCompare(a.date ?? '') ||
		b.activityTime - a.activityTime || b.creationTime - a.creationTime;
}

function prepare(field: SuggestionField, snapshot: SuggestionSnapshot): PreparedSuggestion[] {
	const grouped = new Map<string, PreparedSuggestion>();

	function add(record: SuggestionRecord, source: SuggestionSource) {
		if (!eligible(record)) return;
		const value = source === 'mjbj' ? directoryName(record) : record[field];
		if (typeof value !== 'string' || !value.trim()) return;
		const parsed = parseName(value);
		if (!parsed.key) return;
		// Anumati phones belong to the visitor; they are never referrer contact evidence.
		const ownPhone = source === 'anumati' && field === 'name' ? normalizeMobileNumber(record.phone) : null;
		const phones = source === 'mjbj' ? directoryPhones(record) : ownPhone ? [ownPhone] : [];
		const times = source === 'anumati' ? recordTimes(record) : { creationTime: 0, activityTime: 0 };
		// Each mobile gets its own selectable row; repeated records for that name/mobile
		// pair share one row. A missing mobile never identifies another contact.
		const choices: (string | undefined)[] = phones.length ? phones : [undefined];
		for (const phone of choices) {
			const id = `${source}:${record.id}${phones.length > 1 ? ':' + phone : ''}`;
			const groupKey = `${parsed.title}\u0000${parsed.key}\u0000${phone ?? ''}`;
			const candidate: PreparedSuggestion = {
				id, value, key: parsed.key, words: parsed.key.split(' '), title: parsed.title,
				aliases: source === 'mjbj' && record.old_name ?
					[{ key: normalizeSuggestionName(record.old_name), label: record.old_name }] : [],
				sources: [source], phones: phone ? [phone] : [], ...times,
				historyCount: source === 'anumati' ? 1 : 0,
				recordCount: 1,
				date: source === 'anumati' ? recordDate(record.permission_date) : undefined,
				image: source === 'mjbj' ? record.image : undefined,
			};
			const previous = grouped.get(groupKey);
			if (!previous) {
				grouped.set(groupKey, candidate);
				continue;
			}
			const latestHistory = source === 'anumati' && compareRecency(candidate, previous) < 0;
			const imageConflict = previous.imageConflict ||
				!!(previous.image && candidate.image && previous.image !== candidate.image);
			grouped.set(groupKey, {
				...previous,
				...(latestHistory ? { id, value, ...times, date: candidate.date } : {}),
				historyCount: previous.historyCount + candidate.historyCount,
				recordCount: previous.recordCount + 1,
				sources: Array.from(new Set([...previous.sources, source])),
				aliases: Array.from(new Map([...previous.aliases, ...candidate.aliases].map((alias) => [alias.key, alias])).values()),
				title: previous.title || candidate.title,
				image: imageConflict ? undefined : previous.image || candidate.image,
				imageConflict,
			});
		}
	}

	(snapshot.history ?? []).forEach((record) => add(record, 'anumati'));
	(snapshot.directory ?? []).forEach((record) => add(record, 'mjbj'));
	return Array.from(grouped.values());
}

function matchTier(key: string, words: string[], query: string, tokens: string[]) {
	if (key === query) return 0;
	if (key.startsWith(query)) return 1;
	if (tokens.every((token) => words.some((word) => word.startsWith(token)))) return 2;
	if (tokens.length === 1) {
		const initials = Array.from(query);
		if (initials.length > 1 && initials.length <= words.length &&
			initials.every((letter, index) => words[index].startsWith(letter))) return 3;
	}
	return key.includes(query) ? 4 : null;
}

interface Ranked {
	candidate: PreparedSuggestion;
	tier: number;
	alias: boolean;
	reason?: string;
}

function compare(a: Ranked, b: Ranked) {
	const aHistory = a.candidate.historyCount > 0;
	const bHistory = b.candidate.historyCount > 0;
	if (aHistory !== bHistory) return aHistory ? -1 : 1;
	if (aHistory) {
		const historyOrder = a.tier - b.tier || compareRecency(a.candidate, b.candidate) ||
			Number(a.alias) - Number(b.alias);
		if (historyOrder) return historyOrder;
	}
	// Directory-only results are alphabetical by current name, even when an old name
	// matches with different strength (for example, Aarti versus Aarti (Indore)).
	return a.candidate.key.localeCompare(b.candidate.key) || a.candidate.id.localeCompare(b.candidate.id);
}

/** One source preparation per data revision, shared by Name and Ref; no database reads. */
export function createRecordSuggestionIndex() {
	let previous: SuggestionSnapshot | undefined;
	let candidates: { [field in SuggestionField]: PreparedSuggestion[] } = { name: [], reference: [] };
	const cache = new Map<string, SuggestionResults>();

	return {
		get cacheSize() { return cache.size; },
		clear() {
			previous = undefined;
			candidates = { name: [], reference: [] };
			cache.clear();
		},
		search(field: SuggestionField, input: string, snapshot: SuggestionSnapshot): SuggestionResults {
			if (!previous || previous.history !== snapshot.history || previous.directory !== snapshot.directory ||
				previous.session !== snapshot.session) {
				candidates = { name: prepare('name', snapshot), reference: prepare('reference', snapshot) };
				previous = snapshot;
				cache.clear();
			}
			const parsed = parseName(input);
			const cacheKey = `${field}\u0000${searchText(input)}`;
			const cached = cache.get(cacheKey);
			if (cached) return cached;
			const unavailableSources: SuggestionSource[] = [];
			if (snapshot.history === null) unavailableSources.push('anumati');
			if (snapshot.directory === null) unavailableSources.push('mjbj');
			const best: Ranked[] = [];
			let total = 0;
			if (input.trim().length >= 3 && parsed.key) {
				const tokens = parsed.key.split(' ');
				for (const candidate of candidates[field]) {
					if (parsed.title && parsed.title !== candidate.title) continue;
					let tier = matchTier(candidate.key, candidate.words, parsed.key, tokens);
					let alias = false;
					let reason = tier === 3 ? 'Initials match' : undefined;
					if (tier !== 0) {
						for (const oldName of candidate.aliases) {
							const oldNameTier = matchTier(oldName.key, oldName.key.split(' '), parsed.key, tokens);
							if (oldNameTier !== null && (tier === null || oldNameTier < tier)) {
								tier = oldNameTier;
								alias = true;
								reason = `Matched old name: ${oldName.label}`;
								if (tier === 0) break;
							}
						}
					}
					if (tier === null) continue;
					total++;
					const ranked = { candidate, tier, alias, reason };
					let index = best.findIndex((other) => compare(ranked, other) < 0);
					if (index < 0) index = best.length;
					if (index < RESULT_LIMIT) {
						best.splice(index, 0, ranked);
						if (best.length > RESULT_LIMIT) best.pop();
					}
				}
			}
			const result = {
				total, unavailableSources,
				suggestions: best.map(({ candidate, reason }) => ({ ...candidate, matchReason: reason })),
			};
			if (cache.size >= CACHE_LIMIT) cache.delete(cache.keys().next().value);
			cache.set(cacheKey, result);
			return result;
		},
	};
}
